import { format } from 'date-fns'
import {
  ExternalUserFieldAccess,
  FormField,
  GeckoAnnularRingJSON,
  GeckoCircumferentialWeldJSON,
  GeckoConeCircumferentialWeldJSON,
  GeckoConeCourseJSON,
  GeckoConeRadialWeldJSON,
  GeckoConicalPlateJSON,
  GeckoCourseJSON,
  GeckoFixedSensorJSON,
  GeckoGenericHeadCircumferentialWeldJSON,
  GeckoGenericHeadRadialWeldJSON,
  GeckoHemisphericalHead,
  GeckoHorizontalWeldJSON,
  GeckoLeakDetectionValveJSON,
  GeckoLegacyBoilerAssetModelJSON,
  GeckoLegacyBoilerRepairRegionJSON,
  GeckoLegacyBoilerWallJSON,
  GeckoLegSupportJSON,
  GeckoManwayJSON,
  GeckoNozzleJSON,
  GeckoPipeIntegralSupportJSON,
  GeckoPipelineAssetModelJSON,
  GeckoPipeNonIntegralSupportJSON,
  GeckoPipeWrapJSON,
  GeckoPlateJSON,
  GeckoPolygonalPlateJSON,
  GeckoPressureVesselShell,
  GeckoRectangularManwayJSON,
  GeckoSaddleSupportJSON,
  GeckoSeamWeldJSON,
  GeckoShipDeckBittJSON,
  GeckoShipDeckBulkheadJSON,
  GeckoShipDeckChockJSON,
  GeckoShipDeckCoamingJSON,
  GeckoShipDeckCompartmentJSON,
  GeckoShipDeckDoorJSON,
  GeckoShipDeckDrainJSON,
  GeckoShipDeckHatchJSON,
  GeckoShipDeckLadderJSON,
  GeckoShipDeckLockerJSON,
  GeckoSkirtSupportJSON,
  GeckoTubePlateJSON,
  GeckoVerticalTankAssetModelJSON,
  GeckoVerticalTankFoundation,
  GeckoVerticalTankFoundationJSON,
  GeckoVerticalWeldJSON,
  getFormFieldByDataKey,
  HemisphericalHeadAssetModelJSON,
  InputType,
  MachinedForgingSimpleAssetModelJSON,
  PressureVesselShellAssetModelJSON,
  RolodexEntryJson,
} from 'types'
import {
  GeckoAssetModelType,
  GeckoComponentModelType,
} from 'types/assetModel/assetModelEnums'
import { BaseAssetModelData } from 'types/assetModel/baseAssetModelData'
import {
  compassOrientationFormField,
  installationDateFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoHoodBend,
  HoodBendAssetModelJSON,
} from 'types/assetModel/components/hood/geckoHoodBend'
import {
  GeckoHoodStraight,
  HoodStraightAssetModelJSON,
} from 'types/assetModel/components/hood/geckoHoodStraight'
import {
  GeckoHorizontalTankShell,
  HorizontalTankShellAssetModelJSON,
} from 'types/assetModel/components/horizontalTank/geckoHorizontalTank'
import {
  GeckoLegacyBoilerWalls,
  LegacyBoilerWallsJSON,
} from 'types/assetModel/components/legacyBoiler/geckoLegacyBoilerWalls'
import {
  ConeAssetModelJSON,
  GeckoCone,
} from 'types/assetModel/components/legacyCone/geckoCone'
import {
  GeckoLegacyCone,
  LegacyConeAssetModelJSON,
} from 'types/assetModel/components/legacyCone/geckoLegacyCone'
import { GeckoMachinedForgingSimple } from 'types/assetModel/components/machinedForging/geckoMachinedForgingSimple'
import {
  GeckoLaunchTube,
  LaunchTubeAssetModelJSON,
} from 'types/assetModel/components/missileSilo/geckoLaunchTube'
import {
  DomeAssetModelJSON,
  GeckoDome,
} from 'types/assetModel/components/nuclearContainmentDome/geckoDome'
import {
  DomeWallAssetModelJSON,
  GeckoDomeWall,
} from 'types/assetModel/components/nuclearContainmentDome/geckoDomeWall'
import {
  FlatCircularFloorAssetModelJSON,
  GeckoFlatCircularFloor,
} from 'types/assetModel/components/nuclearContainmentDome/geckoFlatCircularFloor'
import {
  GeckoPipeEccentricReducer,
  PipeEccentricReducerAssetModelJSON,
} from 'types/assetModel/components/piping/geckoPipeEccentricReducer'
import {
  GeckoPipeElbow,
  PipeElbowAssetModelJSON,
} from 'types/assetModel/components/piping/geckoPipeElbow'
import {
  GeckoPipeSection,
  PipeSectionAssetModelJSON,
} from 'types/assetModel/components/piping/geckoPipeSection'
import {
  GeckoPipeTJoint,
  PipeTJointAssetModelJSON,
} from 'types/assetModel/components/piping/geckoPipeTJoint'
import {
  EllipsoidalHeadAssetModelJSON,
  GeckoEllipsoidalHead,
} from 'types/assetModel/components/pressureVessel/geckoEllipsoidalHead'
import {
  GeckoSphereFlatCap,
  SphereFlatCapAssetModelJSON,
} from 'types/assetModel/components/pressureVessel/geckoSphereFlatCap'
import {
  GeckoTorisphericalHead,
  TorisphericalHeadAssetModelJSON,
} from 'types/assetModel/components/pressureVessel/geckoTorisphericalHead'
import {
  GeckoShipDeck,
  ShipDeckAssetModelJSON,
} from 'types/assetModel/components/ship/geckoShipDeck'
import {
  GeckoShipHull,
  ShipHullAssetModelJSON,
} from 'types/assetModel/components/ship/geckoShipHull'
import {
  GeckoTankFlatRoof,
  TankFlatRoofAssetModelJSON,
} from 'types/assetModel/components/verticalTank/geckoTankFlatRoof'
import {
  GeckoTankFloor,
  TankFloorAssetModelJSON,
} from 'types/assetModel/components/verticalTank/geckoTankFloor'
import {
  GeckoVerticalTankShell,
  VerticalTankShellAssetModelJSON,
} from 'types/assetModel/components/verticalTank/geckoVerticalTankShell'
import { GeckoSphericalPlateJSON } from 'types/assetModel/features/containmentDome/geckoSphericalPlate'
import { GeckoCoatingRegionJSON } from 'types/assetModel/features/geckoCoatingRegion'
import { GeckoLinerRegionJSON } from 'types/assetModel/features/geckoLinerRegion'
import { GeckoHoodAssetModelJSON } from 'types/assetModel/geckoHoodAssetModel'
import { RolodexEntryPutJson } from 'types/rolodex/rolodexEntries'
import { getInstallationDateFromString } from 'utils/installationDateHelpers'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import { optionalNumberSchema } from 'utils/zodHelpers'

import { GeckoEntryBase } from './geckoEntry'
export type GeckoAssetModelJSON = {
  name: string
  type: GeckoAssetModelType
  installation_date: string | undefined
  components: Record<string, unknown>
  effective_date: string
  compass_orientation: number | undefined
  plant_north_compass_orientation: number | undefined
}

export const NewGeckoAssetModelJSON = {
  name: 'Asset Model Name',
  effective_date: '',
  compass_orientation: undefined,
  plant_north_compass_orientation: undefined,
  components: {},
  type: GeckoAssetModelType.VerticalTank,
  installation_date: '',
}

export type AssetModelJSONTypes =
  | GeckoAssetModelJSON
  | GeckoVerticalTankAssetModelJSON
  | GeckoLegacyBoilerAssetModelJSON
  | GeckoPipelineAssetModelJSON
  | GeckoHoodAssetModelJSON

export const HorizontalTankAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.HorizontalTankShell,
  GeckoComponentModelType.LegacyCone,
]

export const ShipAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.ShipDeck,
  GeckoComponentModelType.BasicShipHull,
]

export const MissleSiloAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.LaunchTube,
]

export const NuclearContainmentDomeAvailableComponents: GeckoComponentModelType[] =
  [
    GeckoComponentModelType.Dome,
    GeckoComponentModelType.DomeWall,
    GeckoComponentModelType.FlatCircularFloor,
  ]

export const MachinedForgingAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.MachinedForgingSimple,
]

export const PressureVesselAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.PressureVesselShell,
  GeckoComponentModelType.HemisphericalHead,
  GeckoComponentModelType.TorisphericalHead,
  GeckoComponentModelType.EllipsoidalHead,
  GeckoComponentModelType.SphereFlatCap,
]

export type GeckoComponentModel =
  | GeckoHorizontalTankShell
  | GeckoVerticalTankShell
  | GeckoTankFlatRoof
  | GeckoTankFloor
  | GeckoPipeSection
  | GeckoPipeElbow
  | GeckoPipeTJoint
  | GeckoPipeEccentricReducer
  | GeckoLegacyBoilerWalls
  | GeckoShipDeck
  | GeckoShipHull
  | GeckoLegacyCone
  | GeckoCone
  | GeckoLaunchTube
  | GeckoDome
  | GeckoDomeWall
  | GeckoFlatCircularFloor
  | GeckoVerticalTankFoundation
  | GeckoMachinedForgingSimple
  | GeckoPressureVesselShell
  | GeckoHemisphericalHead
  | GeckoTorisphericalHead
  | GeckoEllipsoidalHead
  | GeckoSphereFlatCap
  | GeckoHoodStraight
  | GeckoHoodBend
export type GeckoComponentModelJSON =
  | HorizontalTankShellAssetModelJSON
  | VerticalTankShellAssetModelJSON
  | TankFlatRoofAssetModelJSON
  | TankFloorAssetModelJSON
  | PipeSectionAssetModelJSON
  | PipeElbowAssetModelJSON
  | PipeTJointAssetModelJSON
  | PipeEccentricReducerAssetModelJSON
  | LegacyBoilerWallsJSON
  | ShipDeckAssetModelJSON
  | ShipHullAssetModelJSON
  | LegacyConeAssetModelJSON
  | ConeAssetModelJSON
  | LaunchTubeAssetModelJSON
  | DomeAssetModelJSON
  | DomeWallAssetModelJSON
  | FlatCircularFloorAssetModelJSON
  | GeckoVerticalTankFoundationJSON
  | MachinedForgingSimpleAssetModelJSON
  | HemisphericalHeadAssetModelJSON
  | TorisphericalHeadAssetModelJSON
  | EllipsoidalHeadAssetModelJSON
  | SphereFlatCapAssetModelJSON
  | PressureVesselShellAssetModelJSON
  | HoodStraightAssetModelJSON
  | HoodBendAssetModelJSON
export type GeckoFeaturesJSON =
  | GeckoCourseJSON
  | GeckoPlateJSON
  | GeckoTubePlateJSON
  | GeckoVerticalWeldJSON
  | GeckoHorizontalWeldJSON
  | GeckoCircumferentialWeldJSON
  | GeckoSeamWeldJSON
  | GeckoConeCircumferentialWeldJSON
  | GeckoConeRadialWeldJSON
  | GeckoManwayJSON
  | GeckoRectangularManwayJSON
  | GeckoNozzleJSON
  | GeckoFixedSensorJSON
  | GeckoShipDeckCompartmentJSON
  | GeckoShipDeckBittJSON
  | GeckoShipDeckCoamingJSON
  | GeckoShipDeckChockJSON
  | GeckoShipDeckHatchJSON
  | GeckoShipDeckLockerJSON
  | GeckoShipDeckDrainJSON
  | GeckoShipDeckLadderJSON
  | GeckoShipDeckBulkheadJSON
  | GeckoShipDeckDoorJSON
  | GeckoLegacyBoilerWallJSON
  | GeckoPipeNonIntegralSupportJSON
  | GeckoPipeIntegralSupportJSON
  | GeckoPipeWrapJSON
  | GeckoConeCourseJSON
  | GeckoConicalPlateJSON
  | GeckoLegacyBoilerRepairRegionJSON
  | GeckoPolygonalPlateJSON
  | GeckoCoatingRegionJSON
  | GeckoSphericalPlateJSON
  | GeckoLeakDetectionValveJSON
  | GeckoLinerRegionJSON
  | GeckoAnnularRingJSON
  | GeckoSkirtSupportJSON
  | GeckoSaddleSupportJSON
  | GeckoLegSupportJSON
  | GeckoGenericHeadCircumferentialWeldJSON
  | GeckoGenericHeadRadialWeldJSON

export class GeckoAssetModel<
  TData extends BaseAssetModelData = BaseAssetModelData &
    Record<string, unknown>,
> extends GeckoEntryBase<
  TData,
  {
    organization: string
    site: string
    unit: string
  }
> {
  protected constructor(json: RolodexEntryJson) {
    const recordData = json.data as Record<string, unknown>
    const assetModelType = recordData.type as GeckoAssetModelType
    const effectiveDate = parseDate(recordData.effective_date) ?? new Date()
    const compassOrientation = parseNumber(recordData.compass_orientation)
    const plantNorthCompassOrientation = parseNumber(
      recordData.plant_north_compass_orientation
    )

    const componentsJSON = recordData.components as Record<string, unknown>
    const components = Object.keys(componentsJSON).reduce(
      (resultObject, componentID) => {
        resultObject.set(
          componentID,
          GeckoAssetModel.generateComponent(
            componentID,
            componentsJSON[componentID]
          )
        )
        return resultObject
      },
      new Map<string, GeckoComponentModel>()
    )

    const installationDateStr = recordData.installation_date as
      | string
      | undefined
    const installationDate = getInstallationDateFromString(installationDateStr)

    const baseData = {
      name: parseString(recordData.name) ?? '',
      effective_date: effectiveDate,
      compass_orientation: compassOrientation,
      plant_north_compass_orientation: plantNorthCompassOrientation,
      type: assetModelType,
      components: components,
      installation_date: installationDate,
    } as TData

    super(
      json,
      baseData,
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoAssetModel(json)
  }

  public static generateComponent(
    id: string,
    json: unknown
  ): GeckoComponentModel {
    const componentRecord = json as Record<string, unknown>

    switch (componentRecord.type) {
      case GeckoComponentModelType.TankFlatRoof:
        return GeckoTankFlatRoof.CreateFromJSON(
          id,
          componentRecord as TankFlatRoofAssetModelJSON
        )
      case GeckoComponentModelType.TankFloor:
        return GeckoTankFloor.CreateFromJSON(
          id,
          componentRecord as TankFloorAssetModelJSON
        )
      case GeckoComponentModelType.VerticalTankShell:
        return GeckoVerticalTankShell.CreateFromJSON(
          id,
          componentRecord as VerticalTankShellAssetModelJSON
        )
      case GeckoComponentModelType.HorizontalTankShell:
        return GeckoHorizontalTankShell.CreateFromJSON(
          id,
          componentRecord as HorizontalTankShellAssetModelJSON
        )
      case GeckoComponentModelType.PipeSection:
        return GeckoPipeSection.CreateFromJSON(
          id,
          componentRecord as PipeSectionAssetModelJSON
        )
      case GeckoComponentModelType.PipeElbow:
        return GeckoPipeElbow.CreateFromJSON(
          id,
          componentRecord as PipeElbowAssetModelJSON
        )
      case GeckoComponentModelType.PipeTJoint:
        return GeckoPipeTJoint.CreateFromJSON(
          id,
          componentRecord as PipeTJointAssetModelJSON
        )
      case GeckoComponentModelType.PipeEccentricReducer:
        return GeckoPipeEccentricReducer.CreateFromJSON(
          id,
          componentRecord as PipeEccentricReducerAssetModelJSON
        )
      case GeckoComponentModelType.LegacyBoilerWalls:
        return GeckoLegacyBoilerWalls.CreateFromJSON(
          id,
          componentRecord as LegacyBoilerWallsJSON
        )
      case GeckoComponentModelType.ShipDeck:
        return GeckoShipDeck.CreateFromJSON(
          id,
          componentRecord as ShipDeckAssetModelJSON
        )
      case GeckoComponentModelType.BasicShipHull:
        return GeckoShipHull.CreateFromJSON(
          id,
          componentRecord as ShipHullAssetModelJSON
        )
      case GeckoComponentModelType.LegacyCone:
        return GeckoLegacyCone.CreateFromJSON(
          id,
          componentRecord as LegacyConeAssetModelJSON
        )
      case GeckoComponentModelType.Cone:
        return GeckoCone.CreateFromJSON(
          id,
          componentRecord as ConeAssetModelJSON
        )
      case GeckoComponentModelType.LaunchTube:
        return GeckoLaunchTube.CreateFromJSON(
          id,
          componentRecord as LaunchTubeAssetModelJSON
        )
      case GeckoComponentModelType.Dome:
        return GeckoDome.CreateFromJSON(
          id,
          componentRecord as DomeAssetModelJSON
        )
      case GeckoComponentModelType.FlatCircularFloor:
        return GeckoFlatCircularFloor.CreateFromJSON(
          id,
          componentRecord as FlatCircularFloorAssetModelJSON
        )
      case GeckoComponentModelType.DomeWall:
        return GeckoDomeWall.CreateFromJSON(
          id,
          componentRecord as DomeWallAssetModelJSON
        )
      case GeckoComponentModelType.VerticalTankFoundation:
        return GeckoVerticalTankFoundation.CreateFromJSON(
          id,
          componentRecord as GeckoVerticalTankFoundationJSON
        )
      case GeckoComponentModelType.MachinedForgingSimple:
        return GeckoMachinedForgingSimple.CreateFromJSON(
          id,
          componentRecord as MachinedForgingSimpleAssetModelJSON
        )
      case GeckoComponentModelType.PressureVesselShell:
        return GeckoPressureVesselShell.CreateFromJSON(
          id,
          componentRecord as PressureVesselShellAssetModelJSON
        )
      case GeckoComponentModelType.HemisphericalHead:
        return GeckoHemisphericalHead.CreateFromJSON(
          id,
          componentRecord as HemisphericalHeadAssetModelJSON
        )
      case GeckoComponentModelType.TorisphericalHead:
        return GeckoTorisphericalHead.CreateFromJSON(
          id,
          componentRecord as TorisphericalHeadAssetModelJSON
        )
      case GeckoComponentModelType.EllipsoidalHead:
        return GeckoEllipsoidalHead.CreateFromJSON(
          id,
          componentRecord as EllipsoidalHeadAssetModelJSON
        )
      case GeckoComponentModelType.SphereFlatCap:
        return GeckoSphereFlatCap.CreateFromJSON(
          id,
          componentRecord as SphereFlatCapAssetModelJSON
        )
      case GeckoComponentModelType.HoodStraight:
        return GeckoHoodStraight.CreateFromJSON(
          id,
          componentRecord as HoodStraightAssetModelJSON
        )
      case GeckoComponentModelType.HoodBend:
        return GeckoHoodBend.CreateFromJSON(
          id,
          componentRecord as HoodBendAssetModelJSON
        )
      default:
        throw new Error(
          `Unexpected GeckoAssetModelComponentType! Type provided: ${componentRecord.type}.`
        )
    }
  }

  public asJSON(): GeckoAssetModelJSON {
    return {
      name: this.data.name,
      type: this.data.type,
      installation_date:
        this.data.installation_date !== undefined
          ? format(this.data.installation_date, 'yyyy-MM-dd')
          : undefined,
      components: this.raw.data.components as Record<string, unknown>,
      effective_date: format(this.data.effective_date, 'yyyy-MM-dd'),
      compass_orientation: this.data.compass_orientation,
      plant_north_compass_orientation: this.data.compass_orientation,
    }
  }
}

export function generateAssetModel(
  name: string,
  type: GeckoAssetModelType,
  installation_date: string | undefined,
  components: Record<string, unknown>,
  effective_date: string,
  compass_orientation: number | undefined,
  plant_north_compass_orientation: number | undefined
): GeckoAssetModelJSON {
  return {
    name: name,
    type: type,
    installation_date: installation_date,
    components: components,
    effective_date: effective_date,
    compass_orientation: compass_orientation,
    plant_north_compass_orientation: plant_north_compass_orientation,
  }
}

export function regenerateBaseAssetModel(
  assetModel: GeckoAssetModel
): RolodexEntryPutJson {
  const components = assetModel.raw.data.components as Record<string, unknown>[]
  if (components.length > 0) {
    throw new Error(
      'Base Asset Models should not have any components! Please define a specialized Asset Model to begin adding components!'
    )
  }
  return assetModel.raw
}

export const assetModelFormFields = (assetModelJSON: AssetModelJSONTypes) => {
  return [
    compassOrientationFormField(assetModelJSON.compass_orientation),
    {
      dataKey: 'plant_north_compass_orientation',
      label: 'Plant North Compass Orientation',
      value: assetModelJSON.plant_north_compass_orientation,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    installationDateFormField(assetModelJSON.installation_date, true),
  ]
}

export const getBasicAssetModelJSON = (
  assetModelJSON: AssetModelJSONTypes,
  formFields: FormField[]
) => {
  return generateAssetModel(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    assetModelJSON.type,
    getFormFieldByDataKey(formFields, 'installation_date')?.value as string,
    assetModelJSON.components,
    assetModelJSON.effective_date,
    parseNumber(
      getFormFieldByDataKey(formFields, 'compass_orientation')?.value
    ),
    parseNumber(
      getFormFieldByDataKey(formFields, 'plant_north_compass_orientation')
        ?.value
    )
  )
}
